import React from 'react';
import { formatCurrency, formatDate } from '../utils/formatters';
import ListCard, { ListCards, CardAction } from './ui/ListCard';

const InvoiceTable = ({ invoices, onView, onEdit, onDelete, getStatusBadgeColor }) => {
  const label = (status) => status.charAt(0).toUpperCase() + status.slice(1);
  return (
    <>
    <ListCards>
      {invoices.map((invoice) => {
        const isDraft = invoice.status === 'draft';
        return (
          <ListCard
            key={invoice.id}
            title={invoice.customerName}
            subtitle={`${invoice.invoiceNumber}${invoice.customerPhone ? ` · ${invoice.customerPhone}` : ''}`}
            amount={formatCurrency(invoice.balance > 0 ? invoice.balance : invoice.totalAmount)}
            amountTone={invoice.status === 'overdue' ? 'text-red-600' : invoice.balance > 0 ? 'text-amber-600' : 'text-emerald-600'}
            meta={invoice.balance > 0
              ? `Due ${invoice.dueDate ? formatDate(invoice.dueDate) : '-'} · of ${formatCurrency(invoice.totalAmount)}`
              : `Issued ${invoice.issueDate ? formatDate(invoice.issueDate) : '-'}`}
            badge={<span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${getStatusBadgeColor(invoice.status)}`}>{label(invoice.status)}</span>}
            onClick={isDraft ? undefined : () => onView(invoice)}
            actions={isDraft ? (
              <>
                <CardAction onClick={() => onView(invoice)}>View</CardAction>
                <CardAction tone="primary" onClick={() => onEdit(invoice)}>Edit</CardAction>
                <CardAction tone="danger" onClick={() => onDelete(invoice.id)}>Delete</CardAction>
              </>
            ) : undefined}
          />
        );
      })}
    </ListCards>
    <div className="hidden md:block overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Invoice #
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Customer
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Issue Date
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Due Date
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Amount
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Balance
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {invoices.map((invoice) => (
            <tr
              key={invoice.id}
              className="hover:bg-gray-50 cursor-pointer"
              onClick={() => onView(invoice)}
            >
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-blue-600 hover:text-blue-900">
                  {invoice.invoiceNumber}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900">{invoice.customerName}</div>
                <div className="text-sm text-gray-500">{invoice.customerPhone}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">
                  {invoice.issueDate ? formatDate(invoice.issueDate) : '-'}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className={`text-sm ${
                  invoice.status === 'overdue' ? 'text-red-600 font-medium' : 'text-gray-900'
                }`}>
                  {invoice.dueDate ? formatDate(invoice.dueDate) : '-'}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right">
                <div className="text-sm font-medium text-gray-900">
                  {formatCurrency(invoice.totalAmount)}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right">
                <div className={`text-sm font-medium ${
                  invoice.balance > 0 ? 'text-orange-600' : 'text-green-600'
                }`}>
                  {formatCurrency(invoice.balance)}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(invoice.status)}`}>
                  {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-center">
                <div className="flex items-center justify-center space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onView(invoice);
                    }}
                    className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                    title="View"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </button>
                  {invoice.status === 'draft' && (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(invoice);
                        }}
                        className="text-yellow-600 hover:text-yellow-900 text-sm font-medium"
                        title="Edit"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(invoice.id);
                        }}
                        className="text-red-600 hover:text-red-900 text-sm font-medium"
                        title="Delete"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    </>
  );
};

export default InvoiceTable;
